/**
 * Subscriptions Service
 *
 * Reads subscription snapshots and manual service data from Firestore.
 * Calls the refreshSubscriptionSnapshot callable for on-demand refresh.
 */

import { getFunctions, httpsCallable } from "firebase/functions";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { db, app } from "../config/firebase";

const functions = getFunctions(app, "europe-west1");
const refreshSnapshotFn = httpsCallable<
  Record<string, never>,
  { success: boolean }
>(functions, "refreshSubscriptionSnapshot");

// --- Types ---

export type AlertLevel = "ok" | "warning" | "critical";

export interface BillingServiceCost {
  serviceName: string;
  cost: number;
  trend?: number;
}

export interface BillingCosts {
  services: BillingServiceCost[];
  totalCost: number;
  totalTrend?: number;
  previousMonthTotal?: number;
  previousMonthDays?: number;
  currency: string;
  periodMonth: string;
  daysElapsed: number;
  fetchedAt: string;
  estimated?: boolean;
  previousMonthInvoiced?: boolean;
}

export interface ServiceSnapshot {
  metrics: Record<string, unknown>;
  billing?: BillingCosts;
  status: "ok" | "error";
  error?: string;
  updatedAt?: { toDate: () => Date };
}

export interface ManualService {
  id: string;
  name: string;
  type: "manual";
  plan?: string;
  cost?: string;
  expirationDate?: string;
  notes?: string;
  deepLink?: string;
  updatedAt?: { toDate: () => Date };
}

export interface AutoServiceConfig {
  id: string;
  name: string;
  deepLink: string;
}

export const AUTO_SERVICES: AutoServiceConfig[] = [
  {
    id: "firebase",
    name: "Firebase / GCP",
    // Follows the environment the dashboard was built for, otherwise the prod
    // admin page links to the dev project's usage page.
    deepLink: `https://console.firebase.google.com/project/${
      import.meta.env.VITE_FIREBASE_PROJECT_ID
    }/usage`,
  },
  {
    id: "cloudflare",
    name: "Cloudflare (luca.giorcelli)",
    // Everything (R2, Workers, Pages, domain, deploy) is on luca.giorcelli@gmail.com's account (d51fcff4...).
    // Both tokens (deploy .token + monitoring secret) are scoped to this account.
    deepLink: "https://dash.cloudflare.com/d51fcff41d279e9275cb349fa1b328bc",
  },
  {
    id: "stripe",
    name: "Stripe",
    deepLink: "https://dashboard.stripe.com/billing",
  },
];

export const MANUAL_SERVICE_DEFAULTS: Omit<ManualService, "id">[] = [
  {
    name: "WhatsApp Business",
    type: "manual",
    deepLink: "https://business.facebook.com/latest/whatsapp_manager/phone_numbers/",
  },
  {
    name: "Resend",
    type: "manual",
    deepLink: "https://resend.com/overview",
  },
  {
    name: "Anthropic Claude API",
    type: "manual",
    deepLink: "https://console.anthropic.com/settings/billing",
  },
  {
    name: "Google Play Developer",
    type: "manual",
    deepLink: "https://play.google.com/console/",
  },
  {
    name: "Dominio masterlapse.com",
    type: "manual",
    deepLink: "https://dash.cloudflare.com",
  },
];

// --- Snapshot reads ---

export async function getSnapshot(serviceId: string): Promise<ServiceSnapshot | null> {
  const ref = doc(db, "admin", "subscriptions", "snapshots", serviceId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data() as ServiceSnapshot;
}

export async function getAllSnapshots(): Promise<Record<string, ServiceSnapshot>> {
  const coll = collection(db, "admin", "subscriptions", "snapshots");
  const snap = await getDocs(coll);
  const result: Record<string, ServiceSnapshot> = {};
  snap.forEach((d) => {
    result[d.id] = d.data() as ServiceSnapshot;
  });
  return result;
}

export function subscribeToSnapshots(
  callback: (snapshots: Record<string, ServiceSnapshot>) => void,
  onError?: (error: Error) => void
): () => void {
  const coll = collection(db, "admin", "subscriptions", "snapshots");
  return onSnapshot(
    coll,
    (snap) => {
      const result: Record<string, ServiceSnapshot> = {};
      snap.forEach((d) => {
        result[d.id] = d.data() as ServiceSnapshot;
      });
      callback(result);
    },
    (error) => {
      console.error("subscribeToSnapshots error:", error);
      onError?.(error);
    }
  );
}

// --- Manual services ---

export async function getAllManualServices(): Promise<ManualService[]> {
  const coll = collection(db, "admin", "subscriptions", "services");
  const snap = await getDocs(coll);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as ManualService));
}

export function subscribeToManualServices(
  callback: (services: ManualService[]) => void,
  onError?: (error: Error) => void
): () => void {
  const coll = collection(db, "admin", "subscriptions", "services");
  return onSnapshot(
    coll,
    (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() } as ManualService)));
    },
    (error) => {
      console.error("subscribeToManualServices error:", error);
      onError?.(error);
    }
  );
}

export async function updateManualService(
  serviceId: string,
  data: Partial<ManualService>
): Promise<void> {
  const ref = doc(db, "admin", "subscriptions", "services", serviceId);
  const { id: _, ...rest } = data;
  await setDoc(ref, { ...rest, updatedAt: serverTimestamp() }, { merge: true });
}

// --- Seed manual services ---

export async function seedManualServices(): Promise<void> {
  const existing = await getAllManualServices();
  if (existing.length > 0) return;

  const seedData: Record<string, Omit<ManualService, "id">> = {
    whatsapp: {
      name: "WhatsApp Business",
      type: "manual",
      plan: "Free tier (1,000 service conversations/month)",
      cost: "Free",
      deepLink: "https://business.facebook.com/latest/whatsapp_manager/phone_numbers/",
      notes: "Meta Cloud API. Template messages for alerts.",
    },
    resend: {
      name: "Resend",
      type: "manual",
      plan: "Free (100 emails/day)",
      cost: "Free",
      deepLink: "https://resend.com/overview",
      notes: "Used for transactional emails (cleanup reminders).",
    },
    anthropic: {
      name: "Anthropic Claude API",
      type: "manual",
      plan: "Pay-as-you-go",
      cost: "~$5/month",
      deepLink: "https://console.anthropic.com/settings/billing",
      notes: "Used for i18n translations.",
    },
    playstore: {
      name: "Google Play Developer",
      type: "manual",
      plan: "One-time $25",
      cost: "$25 (paid)",
      expirationDate: "",
      deepLink: "https://play.google.com/console/",
      notes: "Lifetime account, no renewal needed.",
    },
    domain: {
      name: "Dominio masterlapse.com",
      type: "manual",
      plan: "Annual registration",
      cost: "~$10/year",
      expirationDate: "2027-04-15",
      deepLink: "https://dash.cloudflare.com",
      notes: "Registered via Cloudflare (account luca.giorcelli@gmail.com). Auto-renew enabled.",
    },
  };

  const batch = Object.entries(seedData);
  for (const [id, data] of batch) {
    const ref = doc(db, "admin", "subscriptions", "services", id);
    await setDoc(ref, { ...data, updatedAt: serverTimestamp() });
  }
}

// --- Refresh ---

export async function refreshSnapshots(): Promise<void> {
  await refreshSnapshotFn({});
}

// --- Alert helpers ---

export function getAlertLevel(percentage: number): AlertLevel {
  if (percentage >= 95) return "critical";
  if (percentage >= 80) return "warning";
  return "ok";
}

export function getExpirationAlertLevel(daysLeft: number): AlertLevel {
  if (daysLeft <= 7) return "critical";
  if (daysLeft <= 30) return "warning";
  return "ok";
}

export function getDaysUntilExpiration(dateStr: string | undefined): number | null {
  if (!dateStr) return null;
  const now = new Date();
  const expiry = new Date(dateStr);
  return Math.ceil((expiry.getTime() - now.getTime()) / (24 * 3600 * 1000));
}
